import authProvider from '../authProvider';
import tokenExpirationGraceService from '../services/tokenExpirationGraceService';
import statePreservationService from '../services/statePreservationService';

/**
 * Authenticated fetch wrapper that automatically handles token refresh
 * This ensures all API calls include proper authentication and retry on token expiry
 *
 * Implements grace period: if token is expired but in grace period, allows the request
 * to proceed. Only enforces authentication when user actually tries to perform an action.
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const auth = authProvider.auth;

  // Check if token is expired and we need to enforce authentication
  const isTokenExpired = await auth.isTokenExpire();
  const requiresAuth = tokenExpirationGraceService.requiresImmediateAuth();

  // If token is expired and grace period has ended, we must refresh before making request
  if (isTokenExpired && requiresAuth) {
    console.log(
      'Token expired and grace period ended, refreshing token before request...'
    );

    // Save state before attempting refresh (in case we need to redirect)
    statePreservationService.saveState();

    try {
      // Attempt to refresh the token
      await auth.fetchRefreshToken();
      console.log('Token refresh successful before request');

      // End grace period and restore state after successful refresh
      tokenExpirationGraceService.endGracePeriod();
      statePreservationService.restoreState();
    } catch (refreshError) {
      console.error('Token refresh failed before request:', refreshError);
      // If refresh fails, save state and throw error (authProvider will handle redirect)
      statePreservationService.saveState();
      throw new Error('Authentication required. Please log in again.');
    }
  } else if (isTokenExpired && !requiresAuth) {
    // Token expired but in grace period - try background refresh but allow request to proceed
    console.log(
      'Token expired but in grace period, allowing request to proceed'
    );
    auth
      .fetchRefreshToken()
      .then(() => {
        console.log('Background token refresh successful');
        tokenExpirationGraceService.endGracePeriod();
        statePreservationService.restoreState();
      })
      .catch((error) => {
        console.error('Background token refresh failed:', error);
      });
  }

  // Get the current access token
  const accessToken = await auth.getAccessToken();

  console.log('[authenticatedFetch] Token check:', {
    url,
    hasToken: !!accessToken,
    tokenPrefix: accessToken ? accessToken.substring(0, 20) + '...' : 'MISSING',
    isTokenExpired,
    requiresAuth,
  });
  
  if (!accessToken) {
    console.error('[authenticatedFetch] No access token available!');
    throw new Error('No access token available. Please log in again.');
  }

  // Get simulated roles from dev console store (development mode only)
  let simulatedRolesHeader = '';
  if (typeof window !== 'undefined') {
    try {
      const devConsoleStore = (window as any).__devConsoleStore;
      if (devConsoleStore && typeof devConsoleStore.getState === 'function') {
        const state = devConsoleStore.getState();
        if (state?.isRoleSimulationEnabled && state?.simulatedRoles && state.simulatedRoles.length > 0) {
          simulatedRolesHeader = state.simulatedRoles.join(',');
        }
      }
    } catch (e) {
      // Ignore errors accessing store
    }
  }

  // Add authorization header if token exists
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    Authorization: `Bearer ${accessToken}`,
    // Add simulated roles header for development mode (only if enabled)
    ...(simulatedRolesHeader && { 'X-Simulated-Roles': simulatedRolesHeader }),
  };

  // Make the request with auth headers
  // Add timeout to prevent hanging requests
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
  } catch (error) {
    clearTimeout(timeoutId);
    // If it's a network error, provide more context
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        `Network error: Unable to connect to ${url}. Please check if the API server is running.`
      );
    }
    throw error;
  }

  // If we get a 401, try to refresh the token and retry
  if (response.status === 401) {
    console.log('401 error detected, attempting token refresh...');

    // Check if we're in grace period
    if (tokenExpirationGraceService.isInGracePeriod()) {
      console.log(
        '401 error but in grace period, allowing request to fail gracefully'
      );
      // Don't throw error immediately - let the caller handle it
      // This allows the UI to show a message instead of redirecting
      return response;
    }

    // Save state before attempting refresh
    statePreservationService.saveState();

    try {
      // Attempt to refresh the token
      await auth.fetchRefreshToken();
      console.log('Token refresh successful, retrying request...');

      // Get the new token
      const newAccessToken = await auth.getAccessToken();
      console.log('[authenticatedFetch] New token retrieved:', newAccessToken ? newAccessToken.substring(0, 20) + '...' : 'MISSING');
      
      if (!newAccessToken) {
        console.error('[authenticatedFetch] New token is empty!');
        throw new Error('Token refresh succeeded but no access token was returned');
      }

      // Build retry headers properly - start fresh to avoid any issues with the old headers
      const retryHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${newAccessToken}`,
      };
      
      // Merge in any custom headers from options
      if (options.headers) {
        Object.assign(retryHeaders, options.headers);
        // Make sure Authorization is set correctly after merging
        retryHeaders.Authorization = `Bearer ${newAccessToken}`;
      }
      
      // Get simulated roles header if needed
      let simulatedRolesHeader = '';
      if (typeof window !== 'undefined') {
        try {
          const devConsoleStore = (window as any).__devConsoleStore;
          if (devConsoleStore && typeof devConsoleStore.getState === 'function') {
            const state = devConsoleStore.getState();
            if (state?.isRoleSimulationEnabled && state?.simulatedRoles && state.simulatedRoles.length > 0) {
              simulatedRolesHeader = state.simulatedRoles.join(',');
              retryHeaders['X-Simulated-Roles'] = simulatedRolesHeader;
            }
          }
        } catch (e) {
          // Ignore errors accessing store
        }
      }
      
      console.log('[authenticatedFetch] Retrying request with new token, URL:', url);
      console.log('[authenticatedFetch] Retry headers:', {
        ...retryHeaders,
        Authorization: retryHeaders.Authorization ? retryHeaders.Authorization.substring(0, 30) + '...' : 'MISSING',
      });

      const retryResponse = await fetch(url, {
        ...options,
        headers: retryHeaders,
      });

      console.log('[authenticatedFetch] Retry response status:', retryResponse.status, retryResponse.statusText);

      // If retry also returns 401, the token is still invalid
      if (retryResponse.status === 401) {
        console.error('[authenticatedFetch] Retry still returned 401, token refresh may have failed or token is invalid');
        // Don't try to refresh again (would cause infinite loop)
        // Return the error response to let the caller handle it
        return retryResponse;
      }

      // Restore state after successful retry
      tokenExpirationGraceService.endGracePeriod();
      statePreservationService.restoreState();

      return retryResponse;
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      // If refresh fails, save state and throw error
      statePreservationService.saveState();
      throw new Error('Authentication failed. Please log in again.');
    }
  }

  return response;
};

/**
 * Helper function to make authenticated API calls with automatic JSON parsing
 */
export const authenticatedApiCall = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await authenticatedFetch(url, options);

  if (!response.ok) {
    throw new Error(
      `API call failed: ${response.status} ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Helper function for authenticated POST requests
 */
export const authenticatedPost = async <T>(
  url: string,
  data: any,
  options: RequestInit = {}
): Promise<T> => {
  return authenticatedApiCall<T>(url, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * Helper function for authenticated PUT requests
 */
export const authenticatedPut = async <T>(
  url: string,
  data: any,
  options: RequestInit = {}
): Promise<T> => {
  return authenticatedApiCall<T>(url, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options,
  });
};

/**
 * Helper function for authenticated DELETE requests
 */
export const authenticatedDelete = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  return authenticatedApiCall<T>(url, {
    method: 'DELETE',
    ...options,
  });
};
